import type { MatrixClient, Room } from "matrix-js-sdk";
import { noteDismissedCallNotification } from "./selfLeftCalls";

interface CallNotification {
  eventId: string | null;
  callId: string | null;
  deviceId: string | null;
  sessionId: string | null;
  senderTs: number;
  mine: boolean;
}

/**
 * Самое свежее ЖИВОЕ уведомление о звонке в комнате.
 *
 * ПОЧЕМУ ЗДЕСЬ ДВА КАНДИДАТА, А НЕ ОДИН (дефект 04.10). Раньше цикл стоял
 * `if (me && event.getSender() !== me) continue;` — то есть рассматривались
 * ТОЛЬКО свои уведомления. Для исходящего звонка это верно, а для ВХОДЯЩЕГО
 * уведомление прислал звонящий, и совпадений не находилось вообще: callId и
 * eventId оставались null, `if (!callId && !eventId) return;` срабатывал, и
 * m.call.hangup не уходил НИКОГДА. Отсюда «отклоняю на Android — у звонящего
 * ничего не гаснет».
 *
 * Приоритет сохранён прежний: СВОЁ уведомление важнее чужого, иначе в
 * комнате со встречным звонком я бы завершал чужой вызов вместо своего.
 * Чужое берётся только когда своего нет — ровно случай входящего.
 */
export function findActiveCallNotification(
  client: MatrixClient,
  room: Room,
): CallNotification | null {
  const me = client.getUserId();
  let own: CallNotification | null = null;
  let foreign: CallNotification | null = null;

  for (const event of room.getLiveTimeline().getEvents().slice(-80)) {
    if (event.getType() !== "org.matrix.msc4075.rtc.notification") continue;
    const content = event.getContent() as Record<string, unknown>;
    const senderTs = typeof content.sender_ts === "number" ? content.sender_ts : event.getTs();
    const lifetime = typeof content.lifetime === "number" ? content.lifetime : 0;
    if (lifetime > 0 && Date.now() > senderTs + lifetime) continue;

    const mine = !me || event.getSender() === me;
    const current = mine ? own : foreign;
    if (current && senderTs < current.senderTs) continue;

    const candidate: CallNotification = {
      eventId: event.getId() ?? null,
      callId: typeof content.call_id === "string" ? content.call_id : null,
      deviceId: typeof content.device_id === "string" ? content.device_id : null,
      sessionId: typeof content.session_id === "string" ? content.session_id : null,
      senderTs,
      mine,
    };
    if (mine) own = candidate;
    else foreign = candidate;
  }

  return own ?? foreign;
}

/** Publish an explicit, call-scoped termination before MatrixRTC membership leave. */
export async function terminateActiveCall(client: MatrixClient, room: Room): Promise<void> {
  const notification = findActiveCallNotification(client, room);
  if (!notification) return;
  const { callId, deviceId, sessionId, eventId } = notification;
  if (!callId && !eventId) return;
  // Записываем id ДО отправки и независимо от её исхода. Это делает
  // подавление ТОЧЕЧНЫМ и на пути выхода из звонка (rtc.ts teardown),
  // где id уведомления иначе неизвестен. Без этого оставался бы только
  // сравнение по времени, а оно склеивает часы разных устройств.
  if (eventId) noteDismissedCallNotification(room.roomId, eventId);
  // m.call.hangup is a legacy typed event and call_id is mandatory even
  // when our MatrixRTC notification is linked by m.reference. Some MSC4075
  // notifications have no call_id, so use their event id as a stable bridge.
  const effectiveCallId = callId ?? eventId;
  // party_id must identify ME, the sender of this hangup. Reusing the
  // caller's device_id/session_id from an INCOMING notification would claim
  // their party, so remote-owned identifiers are only echoed for our own
  // notification; declining an incoming call signs with our own device.
  const effectivePartyId = notification.mine
    ? (deviceId ?? sessionId ?? client.getDeviceId() ?? "alg-messenger")
    : (client.getDeviceId() ?? "alg-messenger");
  const echoRemoteIds = notification.mine;

  try {
    await client.sendEvent(room.roomId, "m.call.hangup" as never, {
      call_id: effectiveCallId,
      party_id: effectivePartyId,
      ...(echoRemoteIds && deviceId ? { device_id: deviceId } : {}),
      ...(echoRemoteIds && sessionId ? { session_id: sessionId } : {}),
      version: "1",
      reason: "user_hangup",
      ...(eventId ? { "m.relates_to": { rel_type: "m.reference", event_id: eventId } } : {}),
    } as never);
  } catch (error) {
    // Membership leave remains authoritative if this best-effort signal fails.
    console.warn(`[call] termination signal failed room=${room.roomId}:`, error);
  }
}
